import { CalendarMonthIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@40seas/frontend/common/icons';
import { IconButton, styled } from '@mui/material';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import moment, { Moment } from 'moment';
import * as React from 'react';
import { Badge, Stack, Typography } from '../.';
import { Divider } from '../../../muiComponents';
import { DatePickerProps } from './types';

export const DatePicker = ({
    error,
    helperText,
    required,
    fullWidth,
    placeholder = 'Select date',
    eventDay = new Date(),
    showIndicators,
    ...props
}: DatePickerProps) => {
    return (
        <MuiDatePicker
            dayOfWeekFormatter={(dayOfWeek) => dayOfWeek.format('ddd')}
            yearsPerRow={3}
            format="MMM DD, YYYY"
            slots={{
                switchViewIcon: (props) => (
                    <IconButton {...props}>
                        <ChevronDownIcon />
                    </IconButton>
                ),
                leftArrowIcon: () => <ChevronLeftIcon />,
                rightArrowIcon: () => <ChevronRightIcon />,
                inputAdornment: (props) => {
                    const { onClick = null, disabled = false } = React.isValidElement(props?.children) ? props.children.props : {};
                    return (
                        <IconButton onClick={onClick} disabled={disabled}>
                            <CalendarMonthIcon size="large" color="primary" disabled={disabled} />
                        </IconButton>
                    );
                },
                day: (props: PickersDayProps<Moment>) => {
                    const selectedClassname = props.day.isSame(eventDay, 'day') ? ' selected-day' : '';
                    return <CustomPickersDay {...props} className={props.className + selectedClassname} />;
                },
                layout: (props) => (
                    <Stack spacing={0} divider={<Divider />}>
                        {props.children}
                        {showIndicators && (
                            <Stack spacing={1} py={1} px={2}>
                                {props.value && (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Badge color="primary" variant="dot" />
                                        <Typography variant="subtitle3">{'Payment date'}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {moment(props.value).format('MMM DD, YYYY')}
                                        </Typography>
                                    </Stack>
                                )}
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Badge color="neutral" variant="dot" />
                                    <Typography variant="subtitle3">{'Due date'}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {moment(eventDay).format('MMM DD, YYYY')}
                                    </Typography>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                ),
            }}
            views={['year', 'month', 'day']}
            slotProps={{
                dialog: { ...menuProps },
                popper: { ...menuProps },
                textField: {
                    sx: {
                        '& .MuiInputBase-root': {
                            paddingRight: 0,
                            fontSize: '16px',
                            width: props.width,
                        },
                        '&.MuiFormControl-root': {
                            width: 'auto',
                        },
                    },
                    error,
                    fullWidth,
                    helperText: helperText,
                    placeholder,
                    size: 'small',
                    required,
                },
            }}
            {...props}
        />
    );
};

export default DatePicker;

const CustomPickersDay = styled(PickersDay<Moment>)(({ theme }) => ({
    '&.MuiPickersDay-root': {
        borderRadius: '4px',
        '&.MuiPickersDay-today': {
            border: 'none',
            backgroundColor: 'white',
        },
        '&.selected-day': {
            border: '2px solid',
            borderColor: theme.palette.neutral.main,
        },
        '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
        },
    },
}));

const menuProps = {
    sx: {
        '.MuiYearCalendar-root, .MuiMonthCalendar-root': {
            backgroundColor: 'neutral.light',
        },
        '.MuiDateCalendar-root': {
            height: 'auto',
        },
        '.MuiPickersYear-yearButton, .MuiPickersMonth-monthButton': {
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            '&.Mui-selected': {
                backgroundColor: 'primary.main',
                p: {
                    color: 'white',
                },
                '&:focus, &:hover': {
                    backgroundColor: 'primary.main',
                },
            },
        },
    },
};
